import {
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridValueGetter,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Interview } from 'common/models/Interviews';
import GridWithStateWrapper from '../GridWidthStateWrapper';
import { DashboardKioskLink } from 'common/models/Dashboard/DashboardKioskLink';
import { DeleteDashboardKioskLink, GetAllDashboardKioskLinks } from 'services/DashboardService';
import { Box, Switch } from '@mui/material';
import { Link } from 'react-router-dom';

interface Props {
  gridName: string;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}

const spanLinkStyle: React.CSSProperties = { cursor: 'pointer', textDecoration: 'underline' };

export default function DashboardKioskLinksGridComponent({
  gridName,
  loadingHandler,
  errorHandler,
}: Props) {
  const [rows, setRows] = useState<DashboardKioskLink[]>([]);
  const [includeDeleted, setIncludeDeleted] = useState<boolean>(false);

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const apiRef = useGridApiRef();

  const getRowsCallback = useCallback(async () => {
    loadingHandler && loadingHandler(true);
    let res: DashboardKioskLink[] | null = [];
    res = await GetAllDashboardKioskLinks(includeDeleted);
    if (res) setRows(res);
    loadingHandler && loadingHandler(false);
  }, [includeDeleted, loadingHandler, errorHandler]);

  useEffect(() => {
    getRowsCallback();
  }, [getRowsCallback]);

  const handleDelete = async (data: DashboardKioskLink) => {
    const res = await DeleteDashboardKioskLink(data.id);
    if (res) {
      getRowsCallback();
    }
  };

  const handleSwitchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeDeleted(event.target.checked);
  };

  const columns = useMemo<GridColDef[]>(() => {
    const dateValueGetter: GridValueGetter<Interview, any, undefined, string> = (value) => {
      if (value) {
        const d = moment(value);
        if (d.isValid() && d.get('year') > 1) {
          return d.toDate();
        }
      }
      return null;
    };

    const dateRenderer = (params: GridRenderCellParams) => {
      if (params.value) {
        return moment(params.value).format('DD MMM YYYY');
      }
      return params.value;
    };

    const isDeletedRenderer = (params: GridRenderCellParams) => {
      if (params.value == true) {
        return 'Yes';
      }
      return '';
    };

    const actionsRenderer = (params: GridRenderCellParams) => {
      const deleted = params.row.isDeleted;
      let expired = false;
      if (params.row.expiryDate != null && moment(params.row.expiryDate).isSameOrBefore(moment())) {
        expired = true;
      }
      return (
        <>
          {!expired && !deleted && (
            <Link
              target="_blank"
              to={`/kiosk-dashboard/${params.row.token}`}
              style={{ color: 'inherit', textDecoration: 'underline', marginRight: '15px' }}
            >
              Open Kiosk Link
            </Link>
          )}
          {!deleted && (
            <span
              style={{ ...spanLinkStyle }}
              onClick={() => {
                handleDelete(params.row);
              }}
            >
              Delete
            </span>
          )}
        </>
      );
    };

    return [
      { field: 'id', headerName: 'ID', width: 100 },
      { field: 'name', headerName: 'Kiosk Link Name', width: 100 },
      { field: 'dashboardName', headerName: 'Dashboard Name', width: 100 },
      {
        field: 'expiryDate',
        headerName: 'Expiry Date',
        width: 100,
        valueGetter: dateValueGetter,
        renderCell: dateRenderer,
      },
      { field: 'refreshTime', headerName: 'Refresh Time (In Mins)', width: 100 },
      { field: 'createdByName', headerName: 'Created By', width: 100 },
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 100,
        valueGetter: dateValueGetter,
        renderCell: dateRenderer,
      },
      { field: 'isDeleted', headerName: 'Deleted?', width: 100, renderCell: isDeletedRenderer },
      { field: 'deletedByName', headerName: 'Deleted By', width: 100 },
      {
        field: 'deletedDate',
        headerName: 'Deleted Date',
        width: 100,
        valueGetter: dateValueGetter,
        renderCell: dateRenderer,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 250,
        disableColumnMenu: true,
        sortable: false,
        renderCell: actionsRenderer,
      },
    ];
  }, []);

  const defaultHiddenCols: GridColumnVisibilityModel = {
    id: false,
    deletedBy: false,
    deletedByName: false,
    deletedDate: false,
    refreshTime: false,
    createdByName: false,
    createdDate: false,
    isDeleted: false,
  };

  return (
    <>
      <Box>
        <span>Active Only</span>
        <Switch checked={includeDeleted} onChange={handleSwitchChanged} color="primary" />
        <span>All</span>
      </Box>
      <GridWithStateWrapper
        rows={rows}
        columns={columns}
        apiRef={apiRef}
        checkboxSelection={false}
        disableRowSelectionOnClick
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(sm) => setSelectionModel(sm)}
        pagination
        pageSizeOptions={[100, 250, 500, 1000]}
        gridName={gridName}
        defaultViewModel={defaultHiddenCols}
      />
    </>
  );
}
